import React, { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import { PrimaryButton } from "../../UI/DesignStandards";
import {
  EVENT_TEMPLATES,
  buildEventFromTemplate,
  isAllEventTemplate,
  isPrimaryEvent,
  isSidePotEvent,
  matchCalculationType,
  templateAlreadyAdded,
  upcomingPrimaryTypeCount,
} from "../../../utility/eventTemplates";
import { eventHasFees } from "../../../utility/eventFees";
import EventFeeAmountFields from "./EventFeeAmountFields";

const GROUPS = [
  { id: "primary", label: "Primary" },
  { id: "sidepot", label: "Side pots" },
];

const EventTemplatePicker = ({
  events,
  onCreateMany,
  disabled = false,
  catalog = [],
  primaryAmounts = {},
  sidepotAmounts = {},
  onPrimaryAmountChange,
  onSidepotAmountChange,
}) => {
  const [calculationTypes, setCalculationTypes] = useState([]);
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);
  const hasEvents = (events || []).length > 0;
  const [expanded, setExpanded] = useState(!hasEvents);

  useEffect(() => {
    if (hasEvents) setExpanded(false);
  }, [hasEvents]);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("tournamentEventCalculationType")
      .select("id, name, code")
      .then(({ data, error }) => {
        if (cancelled || error) return;
        setCalculationTypes(data || []);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedPrimaryTemplates = EVENT_TEMPLATES.filter(
    (template) => template.group === "primary" && selected[template.id]
  );
  const canSelectAllEvent =
    upcomingPrimaryTypeCount(events, selectedPrimaryTemplates) >= 2;

  const availableTemplates = EVENT_TEMPLATES.filter(
    (template) => !templateAlreadyAdded(template, events)
  );

  useEffect(() => {
    setSelected((prev) => {
      const next = {};
      let changed = false;
      Object.keys(prev).forEach((id) => {
        if (!prev[id]) return;
        const template = EVENT_TEMPLATES.find((item) => item.id === id);
        if (!template || templateAlreadyAdded(template, events)) {
          changed = true;
          return;
        }
        if (isAllEventTemplate(template) && !canSelectAllEvent) {
          changed = true;
          return;
        }
        next[id] = true;
      });
      if (!changed && Object.keys(next).length === Object.keys(prev).length) {
        return prev;
      }
      return next;
    });
  }, [events, canSelectAllEvent]);

  const selectedTemplates = availableTemplates.filter(
    (template) => selected[template.id]
  );
  const selectedSidepots = selectedTemplates.filter(
    (template) => template.group === "sidepot"
  );
  const showPrimaryFees =
    selectedPrimaryTemplates.length > 0 &&
    events.filter(isPrimaryEvent).every((event) => !eventHasFees(event));
  const showSidepotFees =
    selectedSidepots.length > 0 &&
    events.filter(isSidePotEvent).every((event) => !eventHasFees(event));

  const handleToggle = (templateId) => {
    setSelected((prev) => ({ ...prev, [templateId]: !prev[templateId] }));
  };

  const handleCreate = async () => {
    if (disabled || creating || selectedTemplates.length === 0) return;
    const items = selectedTemplates
      .map((template) => {
        const calculationType = matchCalculationType(
          calculationTypes,
          template.calc
        );
        if (!calculationType) return null;
        return {
          template,
          payload: buildEventFromTemplate(
            template,
            calculationType.id,
            events
          ),
        };
      })
      .filter(Boolean);
    if (items.length === 0) return;
    setCreating(true);
    try {
      await onCreateMany(items);
      setSelected({});
      if ((events || []).length + items.length > 0) {
        setExpanded(false);
      }
    } finally {
      setCreating(false);
    }
  };

  if (availableTemplates.length === 0) return null;

  const pickerBody = (
    <div className={hasEvents ? "px-4 pb-4 space-y-4" : "space-y-4"}>
      {GROUPS.map((group) => {
        const templates = availableTemplates.filter(
          (template) => template.group === group.id
        );
        if (templates.length === 0) return null;
        return (
          <div key={group.id}>
            <h3 className="text-sm font-display font-bold text-brand-charcoal mb-2">
              {group.label}
            </h3>
            <div className="space-y-1">
              {templates.map((template) => {
                const calculationType = matchCalculationType(
                  calculationTypes,
                  template.calc
                );
                const checked = Boolean(selected[template.id]);
                const lockedAllEvent =
                  isAllEventTemplate(template) && !canSelectAllEvent;
                const isDisabled =
                  disabled || creating || !calculationType || lockedAllEvent;
                return (
                  <div key={template.id} className="relative group">
                    <label
                      className={`flex items-center space-x-3 p-2 rounded-lg transition-colors ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:bg-brand-cream/80"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (isDisabled) return;
                          handleToggle(template.id);
                        }}
                        disabled={isDisabled}
                        className="w-4 h-4 text-brand-charcoal border-brand-charcoal/20 rounded focus:ring-brand-gold-ink"
                      />
                      <span className="text-sm font-medium text-brand-charcoal">
                        {template.label}
                      </span>
                    </label>
                    {lockedAllEvent ? (
                      <div className="absolute left-8 bottom-full mb-2 hidden group-hover:block z-10">
                        <div className="bg-brand-charcoal text-brand-cream text-xs rounded-lg px-3 py-2 w-56 shadow-lg">
                          Select two primary events first
                          <div className="absolute left-4 top-full border-4 border-transparent border-t-brand-charcoal"></div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {group.id === "primary" && showPrimaryFees ? (
              <div className="mt-3 pl-7">
                <EventFeeAmountFields
                  fees={catalog}
                  amounts={primaryAmounts}
                  onAmountChange={onPrimaryAmountChange}
                  disabled={disabled || creating}
                  events={selectedPrimaryTemplates.map((template) => ({
                    name: template.label,
                    bowlerCount: 0,
                    bowlerQuantity: template.bowlerQuantity,
                  }))}
                />
              </div>
            ) : null}
            {group.id === "sidepot" && showSidepotFees ? (
              <div className="mt-3 pl-7">
                <EventFeeAmountFields
                  fees={catalog}
                  amounts={sidepotAmounts}
                  onAmountChange={onSidepotAmountChange}
                  disabled={disabled || creating}
                  events={selectedSidepots.map((template) => ({
                    name: template.label,
                    bowlerCount: 0,
                    bowlerQuantity: template.bowlerQuantity,
                  }))}
                />
              </div>
            ) : null}
          </div>
        );
      })}
      <div className="flex justify-end pt-1">
        <PrimaryButton
          type="button"
          onClick={handleCreate}
          disabled={disabled || selectedTemplates.length === 0}
          loading={creating}
        >
          {selectedTemplates.length > 0
            ? `Create ${selectedTemplates.length} event${
                selectedTemplates.length === 1 ? "" : "s"
              }`
            : "Create events"}
        </PrimaryButton>
      </div>
    </div>
  );

  if (!hasEvents) {
    return <div className="card-inset p-4 mb-6">{pickerBody}</div>;
  }

  return (
    <div className="card-inset mb-6">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-brand-cream/80 rounded-lg transition-colors"
        aria-expanded={expanded}
      >
        <span className="text-sm font-display font-bold text-brand-charcoal">
          Add from templates
        </span>
        <FaChevronDown
          className={`w-4 h-4 text-brand-muted transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded ? pickerBody : null}
    </div>
  );
};

export default EventTemplatePicker;
